import { CancelOrSubmit } from "common/buttons";
import { Section } from "common/components/Section";
import { FolderArticleCreateForm } from "domain/admin/article/FolderArticleCreateForm";
import { KurssloeyfeArticleCreateForm } from "domain/admin/article/KurssloeyfeArticleCreateForm";
import { ExamSyllabusCreate } from "domain/admin/exam-syllabus/ExamSyllabusCreate";
import { CreateKurssloeyfeForm } from "domain/admin/kurssloeyfe/KurssloeyfeCreateForm";
import { FolderMultipleChoiceCreateForm } from "domain/admin/multiple-choice/FolderMultipleChoiceCreateForm";
import { KurssloeyfeMultipleChoiceCreateForm } from "domain/admin/multiple-choice/KurssloeyfeMultipleChoiceCreateForm";
import * as React from "react";
import {
  AdminStudyProgrammeFragment,
  FolderType,
  MultipleChoiceType,
} from "__generated__/types";
import { FolderCreate } from "../folder/FolderCreate";
import { CreateContentOptions } from "./CreateContentOptions";

export type ContentType =
  | "article"
  | "kurssloeyfe"
  | "multipleChoice"
  | "groupingExercise"
  | "folder"
  | "examSyllabus"
  | "selfAssessmentsContainer"
  | "selfAssessment";
export type Parent =
  | "folder"
  | "selfAssessment"
  | "kurssloeyfe"
  | "selfAssessmentsContainer"
  | "studyProgramme";

interface ContentFormProps {
  parent: Parent;
  parentId: string;
  contentType: ContentType;
  studyProgramme?: AdminStudyProgrammeFragment;
  cancel(): void;
  complete(): void;
}

const ContentForm: React.FC<ContentFormProps> = ({
  parent,
  parentId,
  contentType,
  studyProgramme,
  cancel,
  complete,
}) => {
  switch (contentType) {
    case "article":
      if (parent === "folder" || parent === "selfAssessment") {
        return (
          <FolderArticleCreateForm
            folderId={parentId}
            cancel={cancel}
            complete={complete}
          />
        );
      } else {
        return (
          <KurssloeyfeArticleCreateForm
            kurssloeyfeId={parentId}
            cancel={cancel}
            complete={complete}
          />
        );
      }
    case "kurssloeyfe":
      return (
        <CreateKurssloeyfeForm
          folderId={parentId}
          cancel={cancel}
          complete={complete}
        />
      );
    case "multipleChoice":
    case "groupingExercise":
      //TODO: Possible use another Form for adding MC in kurssloeyfe
      if (parent === "folder" || parent === "selfAssessment") {
        return (
          <FolderMultipleChoiceCreateForm
            folderId={parentId}
            type={
              contentType == "multipleChoice"
                ? MultipleChoiceType.STANDARD
                : MultipleChoiceType.GROUPING
            }
            cancel={cancel}
            complete={complete}
          />
        );
      } else {
        return (
          <KurssloeyfeMultipleChoiceCreateForm
            kurssloeyfeId={parentId}
            type={
              contentType == "multipleChoice"
                ? MultipleChoiceType.STANDARD
                : MultipleChoiceType.GROUPING
            }
            cancel={cancel}
            complete={complete}
          />
        );
      }
    case "folder":
      return (
        <FolderCreate
          parentId={studyProgramme?.id || parentId}
          cancel={cancel}
          complete={complete}
          folderType={FolderType.GENERIC}
        />
      );
    case "selfAssessmentsContainer":
      return (
        <FolderCreate
          parentId={studyProgramme?.id || parentId}
          cancel={cancel}
          complete={complete}
          folderType={FolderType.SELF_ASSESSMENT_EXERCISES_CONTAINER}
        />
      );
    case "selfAssessment":
      return (
        <FolderCreate
          parentId={studyProgramme?.id || parentId}
          cancel={cancel}
          complete={complete}
          folderType={FolderType.SELF_ASSESSMENT_EXERCISES}
        />
      );
    case "examSyllabus":
      return (
        <ExamSyllabusCreate
          cancel={cancel}
          complete={complete}
          parent={studyProgramme}
        />
      );
  }
};

function getTitle(contentType: ContentType | null): string {
  switch (contentType) {
    case "article":
      return "Opprett artikkel";
    case "kurssloeyfe":
      return "Opprett kurssløyfe";
    case "multipleChoice":
      return "Opprett flervalgsoppgave";
    case "groupingExercise":
      return "Opprett grupperingsoppgave";
    case "folder":
      return "Opprett mappe";
    case "selfAssessment":
      return "Opprett selvtest";
    case "examSyllabus":
      return "Opprett pensum";
  }
  return "Opprett innhold";
}

interface Props {
  cancel(): void;
  complete(): void;
  parent: Parent;
  parentId: string;
  studyProgramme?: AdminStudyProgrammeFragment;
}

export const CreateContent: React.FC<React.PropsWithChildren<Props>> = ({
  cancel,
  complete,
  parent,
  parentId,
  studyProgramme,
}) => {
  const [contentType, setContentType] = React.useState<ContentType | null>(
    null,
  );

  return (
    <Section title={getTitle(contentType)}>
      {contentType == null && (
        <>
          <CreateContentOptions parent={parent} handleClick={setContentType} />
          <CancelOrSubmit onCancel={cancel} showSubmit={false} />
        </>
      )}
      {contentType != null && (
        <ContentForm
          contentType={contentType}
          parent={parent}
          parentId={parentId}
          studyProgramme={studyProgramme}
          cancel={cancel}
          complete={complete}
        />
      )}
    </Section>
  );
};
